import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft, PencilSimple, User, GraduationCap, Heart, Users as UsersIcon,
  Phone, Envelope, MapPin, Calendar, ClipboardText, CurrencyDollar,
  Certificate, FileText, ChatCircle, Clock,
} from "@phosphor-icons/react";
import AttendanceHeatmap from "@/components/AttendanceHeatmap";
import { StudentForm } from "./Students";

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon size={16} className="mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
        <div className="text-sm">{value}</div>
      </div>
    </div>
  );
}

export default function StudentProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [tl, setTl] = useState({ payments: [], attendance: [], classes: [] });
  const [editOpen, setEditOpen] = useState(false);

  const load = () => Promise.all([api.get(`/students/${id}`), api.get(`/students/${id}/timeline`)])
    .then(([sr, tr]) => { setS(sr.data); setTl(tr.data); })
    .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { load(); }, [id]);

  if (!s) return <div className="text-sm text-muted-foreground">Loading profile...</div>;

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => nav("/students")} className="gap-2 -ml-3">
        <ArrowLeft size={16} /> Back to Students
      </Button>

      {/* Header card */}
      <Card className="border-border/60 overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-primary via-primary to-destructive/60" />
        <CardContent className="p-6 -mt-12">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="w-24 h-24 rounded-xl bg-card border-4 border-card shadow-md overflow-hidden flex items-center justify-center text-3xl font-bold">
              {s.photo ? <img src={s.photo} alt="" className="w-full h-full object-cover" /> : s.full_name?.charAt(0)}
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                {s.student_number}
              </div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "Manrope" }}>{s.full_name}</h1>
              <div className="flex flex-wrap gap-2 mt-2">
                {s.english_level && <Badge variant="secondary" className="font-mono">Level {s.english_level}</Badge>}
                {s.gender && <Badge variant="outline">{s.gender}</Badge>}
                {s.nationality && <Badge variant="outline">{s.nationality}</Badge>}
                <Badge className={s.status === "active" ? "bg-emerald-500 text-white" : "bg-muted text-foreground"}>
                  {s.status}
                </Badge>
              </div>
            </div>
            <Button
              data-testid="edit-student-btn" variant="outline" className="gap-2 rounded-full"
              onClick={() => setEditOpen(true)}
            >
              <PencilSimple size={14} /> Edit
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope" }}>Edit Student</DialogTitle>
          </DialogHeader>
          <StudentForm initial={s} onSave={() => load()} onClose={() => setEditOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="courses" data-testid="tab-courses">Courses</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="exams" data-testid="tab-exams">Exams</TabsTrigger>
          <TabsTrigger value="certificates" data-testid="tab-certificates">Certificates</TabsTrigger>
          <TabsTrigger value="homework" data-testid="tab-homework">Homework</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <Card className="border-border/60"><CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <User size={16} weight="duotone" /><h3 className="font-semibold" style={{ fontFamily: "Manrope" }}>Personal</h3>
            </div>
            <Row icon={Calendar} label="Date of birth" value={s.date_of_birth} />
            <Row icon={Envelope} label="Email" value={s.email} />
            <Row icon={Phone} label="Phone" value={s.phone} />
            <Row icon={MapPin} label="Address" value={s.address} />
          </CardContent></Card>

          <Card className="border-border/60"><CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <GraduationCap size={16} weight="duotone" /><h3 className="font-semibold" style={{ fontFamily: "Manrope" }}>Academic</h3>
            </div>
            <Row icon={GraduationCap} label="School" value={s.school} />
            <Row icon={ClipboardText} label="Grade" value={s.grade} />
            <Row icon={Certificate} label="Placement Level" value={s.placement_level} />
            <Row icon={Certificate} label="English Level" value={s.english_level} />
          </CardContent></Card>

          <Card className="border-border/60"><CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <UsersIcon size={16} weight="duotone" /><h3 className="font-semibold" style={{ fontFamily: "Manrope" }}>Guardian</h3>
            </div>
            <Row icon={User} label={s.guardian_relation || "Guardian"} value={s.guardian_name} />
            <Row icon={Phone} label="Guardian phone" value={s.guardian_phone} />
            <Row icon={Envelope} label="Guardian email" value={s.guardian_email} />
            <Row icon={Phone} label="Emergency contact" value={`${s.emergency_contact_name || ""} ${s.emergency_contact_phone ? "· " + s.emergency_contact_phone : ""}`} />
          </CardContent></Card>

          <Card className="border-border/60 lg:col-span-2"><CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Heart size={16} weight="duotone" className="text-destructive" /><h3 className="font-semibold" style={{ fontFamily: "Manrope" }}>Medical Information</h3>
            </div>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{s.medical_information || "No medical information on file."}</p>
          </CardContent></Card>

          <Card className="border-border/60"><CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <ChatCircle size={16} weight="duotone" /><h3 className="font-semibold" style={{ fontFamily: "Manrope" }}>Notes</h3>
            </div>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{s.notes || "No notes yet."}</p>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="courses" className="mt-4">
          <Card className="border-border/60"><CardContent className="p-5">
            <h3 className="font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Enrolled Classes</h3>
            {tl.classes.length === 0 ? <p className="text-sm text-muted-foreground">No enrollments yet.</p> :
              <ul className="divide-y divide-border">
                {tl.classes.map((c) => (
                  <li key={c.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.schedule} · {c.room}</div>
                    </div>
                    <Badge variant="outline">{c.student_ids?.length || 0} students</Badge>
                  </li>
                ))}
              </ul>
            }
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="attendance" className="mt-4 space-y-4">
          <Card className="border-border/60"><CardContent className="p-5">
            <h3 className="font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Attendance Heatmap</h3>
            <AttendanceHeatmap studentId={s.id} days={91} />
          </CardContent></Card>
          <Card className="border-border/60"><CardContent className="p-5">
            <h3 className="font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Attendance Log</h3>
            {tl.attendance.length === 0 ? <p className="text-sm text-muted-foreground">No attendance records.</p> :
              <table className="w-full text-sm">
                <thead className="border-b border-border"><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="py-2">Date</th><th>Status</th>
                </tr></thead>
                <tbody>
                  {tl.attendance.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-0">
                      <td className="py-2 font-mono text-xs">{a.date}</td>
                      <td><Badge variant={a.status === "present" ? "default" : a.status === "absent" ? "destructive" : "secondary"}>{a.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <Card className="border-border/60"><CardContent className="p-5">
            <h3 className="font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Payment History</h3>
            {tl.payments.length === 0 ? <p className="text-sm text-muted-foreground">No payments.</p> :
              <table className="w-full text-sm">
                <thead className="border-b border-border"><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="py-2">Invoice</th><th>Amount</th><th>Status</th><th>Due</th>
                </tr></thead>
                <tbody>
                  {tl.payments.map((p) => (
                    <tr key={p.id} className="border-b border-border last:border-0">
                      <td className="py-2 font-mono text-xs">{p.invoice_no}</td>
                      <td className="font-mono">${p.amount}</td>
                      <td><Badge variant={p.status === "paid" ? "default" : p.status === "overdue" ? "destructive" : "secondary"}>{p.status}</Badge></td>
                      <td className="text-xs text-muted-foreground">{p.due_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          </CardContent></Card>
        </TabsContent>

        {["exams", "certificates", "homework", "documents"].map((k) => (
          <TabsContent key={k} value={k} className="mt-4">
            <Card className="border-border/60"><CardContent className="p-8 text-center">
              <FileText size={28} className="mx-auto text-muted-foreground mb-3" />
              <h3 className="font-semibold capitalize" style={{ fontFamily: "Manrope" }}>{k}</h3>
              <p className="text-sm text-muted-foreground mt-1">This section will show {k} for {s.full_name}. Coming next.</p>
            </CardContent></Card>
          </TabsContent>
        ))}

        <TabsContent value="timeline" className="mt-4">
          <Card className="border-border/60"><CardContent className="p-5">
            <h3 className="font-semibold mb-4" style={{ fontFamily: "Manrope" }}>Profile Timeline</h3>
            <ul className="space-y-4">
              {[
                { icon: User, text: `${s.full_name} was registered`, date: s.created_at },
                ...tl.payments.slice(0, 4).map((p) => ({ icon: CurrencyDollar, text: `Invoice ${p.invoice_no} — ${p.status}`, date: p.created_at })),
                ...tl.attendance.slice(0, 4).map((a) => ({ icon: ClipboardText, text: `Marked ${a.status}`, date: a.date })),
              ].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((e, i) => (
                <li key={i} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                    <e.icon size={14} />
                  </div>
                  <div className="flex-1 border-l border-border pl-3 -ml-1 pb-2">
                    <div className="text-sm">{e.text}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{new Date(e.date).toLocaleString()}</div>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
